import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Brand, Sender } from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import {
  type Condition,
  FlowBuilder,
  firstStep,
  hydrateSteps,
  type Step,
} from "../components/flow/FlowBuilder.tsx";
import { RetireActions, ShowArchived } from "../components/Retire.tsx";
import { TimezoneSelect } from "../components/timezones.tsx";
import { Card, Drawer, Empty, TableSkeleton } from "../components/ui.tsx";
import { useUnsavedChanges } from "../components/unsaved.ts";

interface ListRow {
  id: string;
  brand_id: string;
  name: string;
  members: number;
}
interface TemplateRow {
  id: string;
  brandId: string;
  name: string;
  subject: string;
}
interface Enrollment {
  id: string;
  status: string;
  currentStep: number;
  stopReason: string | null;
  nextActionAt: string | null;
  email: string;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function SequencesPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sequences = useQuery({
    queryKey: ["sequences", showArchived],
    queryFn: () =>
      api.get<
        {
          id: string;
          campaign_id: string;
          name: string;
          status: string;
          brand_id: string;
          brand_name: string;
          steps: number;
          archived_at: string | null;
        }[]
      >(`/sequences${showArchived ? "?archived=true" : ""}`),
  });

  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["sequences"] });
  };

  /**
   * Addressed by campaign id, not sequence id: a sequence is a campaign with
   * steps attached, and archiving is a property of the campaign.
   */
  const archive = useMutation({
    mutationFn: (campaignId: string) => api.post(`/campaigns/${campaignId}/archive`),
    onSuccess: () => {
      refresh();
      toast("Sequence archived");
    },
    onError: (err: Error) => setError(err.message),
  });
  const unarchive = useMutation({
    mutationFn: (campaignId: string) => api.post(`/campaigns/${campaignId}/unarchive`),
    onSuccess: () => {
      refresh();
      toast("Sequence restored");
    },
    onError: (err: Error) => setError(err.message),
  });
  const remove = useMutation({
    mutationFn: (campaignId: string) => api.del(`/campaigns/${campaignId}`),
    onSuccess: () => {
      refresh();
      toast("Sequence deleted");
    },
    onError: (err: Error) => setError(err.message),
  });
  const pending = archive.isPending || unarchive.isPending || remove.isPending;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Sequences</h1>
        <p>Multi-step follow-up that stops when someone replies.</p>
      </div>

      <div className="toolbar">
        <ShowArchived value={showArchived} onChange={setShowArchived} />
        <div className="spacer" />
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          + New sequence
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {creating && (
        <Drawer
          title="New sequence"
          subtitle="Follow-up that sends step by step and stops when someone replies."
          onClose={() => setCreating(false)}
        >
          <NewSequence
            onDone={() => {
              setCreating(false);
              void qc.invalidateQueries({ queryKey: ["sequences"] });
            }}
          />
        </Drawer>
      )}

      <Card>
        {sequences.isLoading ? (
          <TableSkeleton />
        ) : sequences.data?.length === 0 ? (
          <Empty>
            No sequences yet. A sequence follows up on its own and stops when someone replies.{" "}
            <button type="button" className="btn small" onClick={() => setCreating(true)}>
              + New sequence
            </button>
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Sequence</th>
                <th className="num">Steps</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sequences.data?.map((s) => (
                <tr key={s.id} className={openId === s.id ? "row-selected" : ""}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div style={{ color: "var(--gray-500)", fontSize: 11 }}>{s.brand_name}</div>
                  </td>
                  <td className="num">{s.steps}</td>
                  <td>
                    <span className="pill mute">{s.status}</span>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <Link className="btn ghost small" to={`/campaigns/${s.campaign_id}`}>
                      Report
                    </Link>{" "}
                    <button
                      type="button"
                      className="btn ghost small"
                      onClick={() => setOpenId(s.id)}
                    >
                      Manage
                    </button>{" "}
                    <RetireActions
                      archived={s.archived_at !== null}
                      pending={pending}
                      onArchive={async () => {
                        if (
                          await confirmAction({
                            title: `Archive ${s.name}?`,
                            message:
                              "Anyone part way through it stops receiving, and unarchiving does not restart them. Reports are kept.",
                            confirmLabel: "Archive sequence",
                            destructive: true,
                          })
                        ) {
                          if (openId === s.id) setOpenId(null);
                          archive.mutate(s.campaign_id);
                        }
                      }}
                      onUnarchive={() => unarchive.mutate(s.campaign_id)}
                      onDelete={async () => {
                        if (
                          await confirmAction({
                            title: `Delete ${s.name}?`,
                            message: "This can't be undone.",
                            confirmLabel: "Delete sequence",
                            destructive: true,
                          })
                        )
                          remove.mutate(s.campaign_id);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/*
        Keyed, so switching sequences remounts the panel. Both this component
        and the flow editor inside it hold per-sequence state that is seeded
        once from its query; without a fresh instance the previous sequence's
        steps stay on screen next to the new one's enrollments.
      */}
      {openId && (
        <SequenceDetail
          key={openId}
          id={openId}
          name={sequences.data?.find((s) => s.id === openId)?.name ?? "Sequence"}
          brandName={sequences.data?.find((s) => s.id === openId)?.brand_name ?? ""}
          brandId={sequences.data?.find((s) => s.id === openId)?.brand_id ?? ""}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function SequenceDetail({
  id,
  name,
  brandName,
  brandId,
  onClose,
}: {
  id: string;
  name: string;
  brandName: string;
  brandId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [listId, setListId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enrollments = useQuery({
    queryKey: ["enrollments", id],
    queryFn: () => api.get<Enrollment[]>(`/sequences/${id}/enrollments`),
    refetchInterval: 10_000,
  });
  const lists = useQuery({
    queryKey: ["lists"],
    queryFn: () => api.get<ListRow[]>("/audience/lists"),
  });

  const brandLists = (lists.data ?? []).filter((l) => l.brand_id === brandId);

  const enroll = useMutation({
    mutationFn: () =>
      api.post<{ enrolled: number; skipped: number }>(`/sequences/${id}/enroll`, { listId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["enrollments", id] });
      toast("Enrolled");
    },
    onError: (err: Error) => setError(err.message),
  });

  const counts = (enrollments.data ?? []).reduce<Record<string, number>>((acc, e) => {
    acc[e.status] = (acc[e.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Drawer title={name} subtitle={brandName} wide onClose={onClose}>
      <SendWindowCard id={id} />
      <EditFlow id={id} />

      <Card title="Enroll contacts">
        <div className="card-body">
          {error && <div className="error">{error}</div>}
          {enroll.data && (
            <div className="notice">
              Enrolled {enroll.data.enrolled}. Skipped {enroll.data.skipped} already enrolled,
              suppressed or unsubscribed.
            </div>
          )}
          <div className="toolbar" style={{ marginBottom: 0 }}>
            {/* This sequence's brand only: consent and unsubscribes are per
                brand, and enrolling another brand's list would check the wrong
                one. The API refuses it too. */}
            <select value={listId} onChange={(e) => setListId(e.target.value)}>
              <option value="">Choose a list…</option>
              {brandLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.members.toLocaleString()})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              disabled={!listId || enroll.isPending}
              onClick={() => enroll.mutate()}
            >
              Enroll
            </button>
          </div>
          {brandLists.length === 0 && (
            <div className="hint" style={{ marginTop: 8 }}>
              No lists for this sequence&rsquo;s brand. A list belongs to one brand, because consent
              and unsubscribes are per brand — import or create one on{" "}
              <Link to="/imports">Lists &amp; imports</Link>.
            </div>
          )}
        </div>
      </Card>

      <Card
        title="Enrollments"
        subtitle={Object.entries(counts)
          .map(([k, v]) => `${v} ${k}`)
          .join(" · ")}
      >
        {enrollments.isLoading ? (
          <TableSkeleton />
        ) : enrollments.data?.length === 0 ? (
          <Empty>Nobody enrolled yet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Contact</th>
                <th className="num">Step</th>
                <th>Status</th>
                <th>Next action</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.data?.slice(0, 100).map((e) => (
                <tr key={e.id}>
                  <td className="mono">{e.email}</td>
                  <td className="num">{e.currentStep + 1}</td>
                  <td>
                    <span
                      className={`pill ${
                        e.status === "active" ? "info" : e.status === "stopped" ? "warn" : "mute"
                      }`}
                    >
                      {e.status}
                    </span>
                    {e.stopReason && (
                      <span style={{ color: "var(--gray-500)", fontSize: 11, marginLeft: 6 }}>
                        {e.stopReason}
                      </span>
                    )}
                  </td>
                  <td className="mono">
                    {e.nextActionAt ? new Date(e.nextActionAt).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </Drawer>
  );
}

/**
 * Editing an existing flow.
 *
 * The API reports how many leading steps are already in flight; those are
 * handed to the builder as locked rather than being filtered out, so the shape
 * an operator sees is the shape that is actually running.
 */
function EditFlow({ id }: { id: string }) {
  const qc = useQueryClient();
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const sequence = useQuery({
    queryKey: ["sequence", id],
    queryFn: () =>
      api.get<{
        brandId: string;
        lockedSteps: number;
        steps: { templateId: string; delayHours: number; condition: Condition }[];
      }>(`/sequences/${id}`),
  });
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateRow[]>("/templates"),
  });

  // Loaded once; afterwards the builder owns the draft, so a background refetch
  // cannot discard edits in progress.
  const [savedSteps, setSavedSteps] = useState<string | null>(null);
  useEffect(() => {
    if (sequence.data && steps === null) {
      const loaded = hydrateSteps(sequence.data.steps);
      setSteps(loaded);
      setSavedSteps(stepsKey(loaded));
    }
  }, [sequence.data, steps]);
  useUnsavedChanges(steps !== null && savedSteps !== null && stepsKey(steps) !== savedSteps);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/sequences/${id}`, {
        steps: (steps ?? []).map(({ uid: _uid, unit: _unit, ...step }) => step),
      }),
    onSuccess: () => {
      toast("Sequence saved");
      setError(null);
      setSaved(true);
      setSavedSteps(stepsKey(steps ?? []));
      void qc.invalidateQueries({ queryKey: ["sequences"] });
      void qc.invalidateQueries({ queryKey: ["sequence", id] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setSaved(false);
    },
  });

  const locked = sequence.data?.lockedSteps ?? 0;
  const brandTemplates = (templates.data ?? []).filter((t) => t.brandId === sequence.data?.brandId);

  return (
    <Card
      title="Flow"
      subtitle={
        locked > 0
          ? `${locked} step${locked === 1 ? "" : "s"} already sent — fixed`
          : "Nobody enrolled yet, so anything can change"
      }
    >
      <div className="card-body">
        {error && <div className="error">{error}</div>}
        {saved && !save.isPending && <div className="notice">Flow saved.</div>}

        {steps && (
          <>
            <FlowBuilder
              steps={steps}
              templates={brandTemplates}
              lockedCount={locked}
              onChange={(next) => {
                setSteps(next);
                setSaved(false);
              }}
            />
            <button
              type="button"
              className="btn"
              style={{ marginTop: 12 }}
              disabled={save.isPending || steps.some((s) => !s.templateId)}
              onClick={() => save.mutate()}
            >
              Save flow
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

function NewSequence({ onDone }: { onDone: () => void }) {
  const [brandId, setBrandId] = useState("");
  const [senderId, setSenderId] = useState("");
  const [name, setName] = useState("");
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  // On by default: 9 to 5 should mean the recipient's 9 to 5.
  const [perRecipient, setPerRecipient] = useState(true);
  const [stopOn, setStopOn] = useState<string[]>(["reply", "unsubscribe"]);
  const [replyForwardTo, setReplyForwardTo] = useState("");
  const [steps, setSteps] = useState<Step[]>([firstStep()]);
  const [error, setError] = useState<string | null>(null);

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });
  const senders = useQuery({
    queryKey: ["senders"],
    queryFn: () => api.get<Sender[]>("/config/senders"),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<{ id: string; brandId: string }[]>("/config/domains"),
  });
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateRow[]>("/templates"),
  });

  useEffect(() => {
    if (!brandId && brands.data?.[0]) setBrandId(brands.data[0].id);
  }, [brandId, brands.data]);

  const brandDomainIds = new Set(
    (domains.data ?? []).filter((d) => d.brandId === brandId).map((d) => d.id),
  );
  const brandSenders = (senders.data ?? []).filter((s) => brandDomainIds.has(s.domainId));
  const brandTemplates = (templates.data ?? []).filter((t) => t.brandId === brandId);

  const create = useMutation({
    mutationFn: () =>
      api.post("/sequences", {
        brandId,
        senderId,
        name,
        createdBy: "web",
        sendWindow: { days, start, end, timezone, perRecipient },
        stopOn,
        replyForwardTo: replyForwardTo.trim() || null,
        // uid and unit are client concerns; the API orders steps by position
        // and always takes the delay in hours.
        steps: steps.map(({ uid: _uid, unit: _unit, ...step }) => step),
      }),
    onSuccess: () => {
      onDone();
      toast("Sequence created");
    },
    onError: (err: Error) => setError(err.message),
  });

  useUnsavedChanges(Boolean(name.trim()));

  return (
    <Card>
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}

        <div className="row">
          <div className="field">
            <label htmlFor="s-name">Name</label>
            <input id="s-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="s-brand">Brand</label>
            <select
              id="s-brand"
              value={brandId}
              onChange={(e) => {
                setBrandId(e.target.value);
                setSenderId("");
              }}
            >
              {brands.data?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="s-sender">From</label>
          <select
            id="s-sender"
            value={senderId}
            onChange={(e) => setSenderId(e.target.value)}
            required
          >
            <option value="">Choose a sender…</option>
            {brandSenders.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName} — {s.email}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="s-days">Send window</label>
          <div className="daypicker" id="s-days">
            {DAYS.map((label, i) => {
              const iso = i + 1;
              const on = days.includes(iso);
              return (
                <button
                  key={label}
                  type="button"
                  className={`day${on ? " on" : ""}`}
                  onClick={() => setDays(on ? days.filter((d) => d !== iso) : [...days, iso])}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <label className="check" style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={perRecipient}
              onChange={(e) => setPerRecipient(e.target.checked)}
            />{" "}
            In each recipient&rsquo;s own time zone
          </label>
          <div style={{ marginTop: 8 }}>
            <TimezoneSelect value={timezone} onChange={setTimezone} />
          </div>
          <div className="hint">
            {perRecipient
              ? "Used for contacts whose zone is unknown: one set on the contact, or worked out from a US or Canadian phone number."
              : "Every step goes by this zone."}
          </div>
          <div className="hint">
            Cold outreach that arrives at 3am reads as automated. Steps that come due outside the
            window wait for it to open.
          </div>
        </div>

        <div className="field">
          <label htmlFor="s-forward">Forward replies to</label>
          <input
            id="s-forward"
            type="email"
            value={replyForwardTo}
            onChange={(e) => setReplyForwardTo(e.target.value)}
            placeholder="nobody — replies stay in the Replies inbox"
          />
          <div className="hint">
            Replies are captured here first and relayed on, rather than addressed straight to this
            person. The Reply-To on a sequence carries the token that matches a reply back to its
            enrollment, so pointing it at a mailbox would stop the sequence noticing replies at all
            — and reactivation reporting counts replies and nothing else. Whoever receives the relay
            can reply to it directly; it comes back addressed to the contact.
          </div>
        </div>

        <div className="field">
          <label htmlFor="s-stop">Stop when the contact</label>
          <div className="daypicker" id="s-stop">
            {["reply", "click", "unsubscribe"].map((signal) => {
              const on = stopOn.includes(signal);
              return (
                <button
                  key={signal}
                  type="button"
                  className={`day${on ? " on" : ""}`}
                  onClick={() =>
                    setStopOn(on ? stopOn.filter((s) => s !== signal) : [...stopOn, signal])
                  }
                >
                  {signal}s
                </button>
              );
            })}
          </div>
          <div className="hint">
            Automated opens and link-scanner clicks never count, so a security gateway cannot end a
            sequence on somebody&rsquo;s behalf.
          </div>
        </div>

        <div className="field">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: heading for the flow below. */}
          <label>Flow</label>
          {brandTemplates.length === 0 ? (
            <div className="hint">
              This brand has no templates yet. Create one before building a sequence.
            </div>
          ) : (
            <FlowBuilder steps={steps} templates={brandTemplates} onChange={setSteps} />
          )}
        </div>

        <button className="btn" type="submit" disabled={create.isPending}>
          Create sequence
        </button>
      </form>
    </Card>
  );
}

/** A draft's content, without the editor's own row ids and units, for "is it saved?". */
function stepsKey(steps: Step[]): string {
  return JSON.stringify(steps.map(({ uid: _uid, unit: _unit, ...step }) => step));
}

interface Window {
  days: number[];
  start: string;
  end: string;
  timezone: string;
  perRecipient?: boolean;
}

/** The sequence's sending hours, and whether they are each recipient's own. */
function SendWindowCard({ id }: { id: string }) {
  const qc = useQueryClient();
  const sequence = useQuery({
    queryKey: ["sequence", id],
    queryFn: () => api.get<{ sendWindow: Window }>(`/sequences/${id}`),
  });
  const save = useMutation({
    mutationFn: (sendWindow: Window) => api.patch(`/sequences/${id}`, { sendWindow }),
    onSuccess: () => {
      toast("Send window saved");
      void qc.invalidateQueries({ queryKey: ["sequence", id] });
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  const w = sequence.data?.sendWindow;
  if (!w) return null;
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <Card
      title="Send window"
      subtitle={`${w.days.map((d) => dayNames[d - 1]).join(", ")} · ${w.start}–${w.end}`}
    >
      <div className="card-body">
        <label className="check">
          <input
            type="checkbox"
            checked={Boolean(w.perRecipient)}
            disabled={save.isPending}
            onChange={(e) => save.mutate({ ...w, perRecipient: e.target.checked })}
          />{" "}
          In each recipient&rsquo;s own time zone
        </label>
        <div className="row" style={{ marginTop: 10 }}>
          <div className="field">
            <label htmlFor={`sw-tz-${id}`}>
              {w.perRecipient ? "Fallback time zone" : "Time zone"}
            </label>
            <TimezoneSelect
              id={`sw-tz-${id}`}
              value={w.timezone}
              onChange={(timezone) => save.mutate({ ...w, timezone })}
            />
          </div>
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          {w.perRecipient
            ? "A contact's zone is the one set on them, else their US or Canadian phone's area code, else the fallback. Steps already scheduled move at their next check."
            : "Every recipient gets these hours in this one zone."}
        </p>
      </div>
    </Card>
  );
}
